import React, { useState, useMemo, useEffect, useRef } from "react"
import {
    Box,
    TextField,
    Typography,
    IconButton,
    Button,
    Chip,
    Checkbox,
    Dialog,
    DialogTitle,
    DialogContent,
} from "@mui/material"
import DeleteIcon from "@mui/icons-material/Delete"
import EditIcon from "@mui/icons-material/Edit"
import AddIcon from "@mui/icons-material/Add"
import CloseIcon from "@mui/icons-material/Close"
import { useLocalization } from "../../../providers/LocalizationProvider"

/* -------------------- DynamicCard (Reusable) -------------------- */
const DynamicCard = ({
    title,
    description,
    timeOptions = [],
    selectedTime = "",
    isEditing,
    onEdit,
    onDelete,
    onChange,
    showTimeOptions = true,
}) => {
    const handleTitleChange = (e) => onChange?.({ title: e.target.value })
    const handleDescriptionChange = (e) => onChange?.({ description: e.target.value })
    const handleTimeChange = (t) => onChange?.({ selectedTime: t })
    const { voiceiiTags } = useLocalization()
    return (
        <Box
            className="e-task-form-card"
            sx={{
                borderRadius: "12px",
                border: "1px solid rgba(0,0,0,0.08)",
                boxShadow: "0 1px 2px rgba(0,0,0,0.06)",
                p: 2,
                backgroundColor: "background.paper",
            }}
        >
            {isEditing ? (
                <Box sx={{ display: "flex", flexDirection: "column", gap: 1 }}>
                    <Box
                        className="e-task-form-header"
                        sx={{
                            display: "flex",
                            alignItems: { xs: "stretch", sm: "flex-start" },
                            flexDirection: { xs: "column", sm: "row" },
                            gap: 1,
                        }}
                    >
                        <TextField
                            className="e-task-name-input"
                            fullWidth
                            size="small"
                            placeholder={voiceiiTags("Title")}
                            value={title || ""}
                            onChange={handleTitleChange}
                            sx={{ flex: 1, minWidth: { xs: "unset", sm: 200 } }}
                        />

                        <Box
                            sx={{
                                display: "flex",
                                alignItems: "center",
                                gap: 1,
                                flexWrap: "wrap",
                            }}
                        >
                            {showTimeOptions &&
                                Array.isArray(timeOptions) &&
                                timeOptions.length > 0 &&
                                timeOptions.map((t) => (
                                    <Chip
                                        key={t}
                                        label={t}
                                        clickable
                                        onClick={() => handleTimeChange(t)}
                                        className={`e-duration-chip ${
                                            t === selectedTime ? "selected" : ""
                                        }`}
                                        sx={{
                                            bgcolor:
                                                t === selectedTime
                                                    ? "rgba(0,167,111,0.15)"
                                                    : undefined,
                                            border:
                                                t === selectedTime
                                                    ? "1px solid #00A76F"
                                                    : undefined,
                                        }}
                                    />
                                ))}

                            <IconButton
                                onClick={onDelete}
                                className="e-task-delete-btn"
                                aria-label="delete"
                            >
                                <DeleteIcon />
                            </IconButton>
                        </Box>
                    </Box>

                    <TextField
                        fullWidth
                        multiline
                        rows={2}
                        placeholder={voiceiiTags("Description")}
                        value={description || ""}
                        onChange={handleDescriptionChange}
                        className="e-task-instructions-input"
                    />

                    <Box sx={{ display: "flex", justifyContent: "flex-end" }}>
                        <button
                            className="btn btn-secondary text-capitalize fw-bold py-1 px-2"
                            onClick={() => onEdit?.(false)}
                        >
                            {voiceiiTags("Minimize")}
                        </button>
                    </Box>
                </Box>
            ) : (
                <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                    <Box sx={{ flex: 1, minWidth: 0 }}>
                        <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                            <Typography
                                sx={{
                                    fontWeight: 600,
                                    overflow: "hidden",
                                    textOverflow: "ellipsis",
                                    whiteSpace: "nowrap",
                                }}
                            >
                                {title || ""}
                            </Typography>

                            {showTimeOptions && selectedTime && (
                                <Box
                                    component="span"
                                    sx={{
                                        px: 1,
                                        py: 0.25,
                                        borderRadius: "999px",
                                        fontSize: 12,
                                        fontWeight: 600,
                                        color: "#00A76F",
                                        bgcolor: "rgba(0,167,111,0.12)",
                                        border: "1px solid rgba(0,167,111,0.35)",
                                        whiteSpace: "nowrap",
                                    }}
                                >
                                    {selectedTime}
                                </Box>
                            )}
                        </Box>

                        <Typography
                            variant="body2"
                            sx={{ mt: 0.5, color: "text.secondary" }}
                            noWrap
                        >
                            {description || ""}
                        </Typography>
                    </Box>

                    <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
                        <IconButton
                            onClick={() => onEdit?.(true)}
                            className="e-task-edit-btn"
                            aria-label="edit"
                        >
                            <EditIcon />
                        </IconButton>

                        <IconButton
                            onClick={onDelete}
                            className="e-task-delete-btn"
                            aria-label="delete"
                        >
                            <DeleteIcon />
                        </IconButton>
                    </Box>
                </Box>
            )}
        </Box>
    )
}

/* -------------------- Task Inline Form -------------------- */

const TaskInlineForm = ({ task, taskSchema, onUpdate, onDelete }) => {
    const durationField = taskSchema.fields?.find((f) => f.key === "duration_minutes")

    const [isEditingCard, setIsEditingCard] = useState(!task.task_name)

    const showTimeOptions = Boolean(durationField)
    const rawOptions = durationField?.options || [5, 15, 30]
    const timeOptions = showTimeOptions ? rawOptions.map((m) => `${m}m`) : []

    const selectedTime = showTimeOptions
        ? `${task.duration_minutes !== undefined && task.duration_minutes !== null ? task.duration_minutes : durationField?.default || 15}m`
        : ""

    const handleCardChange = (patch) => {
        if (!patch || typeof patch !== "object") return

        if (patch.title !== undefined) {
            onUpdate({ ...task, task_name: patch.title })
            return
        }

        if (patch.description !== undefined) {
            onUpdate({ ...task, instructions: patch.description })
            return
        }

        if (patch.selectedTime !== undefined) {
            const minutes = Number(String(patch.selectedTime).replace("m", ""))
            onUpdate({
                ...task,
                duration_minutes: Number.isFinite(minutes) ? minutes : task.duration_minutes,
            })
        }
    }

    return (
        <DynamicCard
            title={task.task_name}
            description={task.instructions}
            timeOptions={timeOptions}
            selectedTime={selectedTime}
            isEditing={isEditingCard}
            onEdit={setIsEditingCard}
            onDelete={onDelete}
            onChange={handleCardChange}
            showTimeOptions={showTimeOptions}
        />
    )
}

/* -------------------- Template Task Modal -------------------- */

const TemplateTaskModal = ({ open, onClose, templateTasks, onAdd }) => {
    const [selected, setSelected] = useState([])
    const { voiceiiTags } = useLocalization()
    const toggle = (idx) => {
        setSelected((prev) => (prev.includes(idx) ? prev.filter((i) => i !== idx) : [...prev, idx]))
    }

    const handleAdd = () => {
        onAdd(templateTasks.filter((_, i) => selected.includes(i)))
        setSelected([])
        onClose()
    }

    return (
        <Dialog
            open={open}
            fullWidth
            maxWidth="sm"
            onClose={onClose}
            className="e-template-task-modal"
        >
            <Box className="e-template-modal-header">
                <Box className="e-template-modal-title-section">
                    <DialogTitle className="e-template-modal-title">
                        {voiceiiTags("Select Template Tasks")}
                    </DialogTitle>
                    <Typography className="e-template-modal-subtitle">
                        {voiceiiTags("Choose tasks from the template to add to your care plan.")}
                    </Typography>
                </Box>
                <CloseIcon className="e-template-modal-close" onClick={onClose} />
            </Box>

            <DialogContent className="e-template-modal-content">
                {templateTasks.map((task, idx) => (
                    <Box key={idx} className="e-template-task-card">
                        <Checkbox
                            checked={selected.includes(idx)}
                            onChange={() => toggle(idx)}
                            className="e-template-task-checkbox"
                        />
                        <Box className="e-template-task-info">
                            <Typography className="e-template-task-name">
                                {task.task_name}
                            </Typography>
                            <Typography className="e-template-task-description">
                                {task.instructions}
                            </Typography>
                        </Box>
                        <Chip
                            label={`${task.duration_minutes}m`}
                            className="e-template-task-duration"
                        />
                    </Box>
                ))}
            </DialogContent>

            <Box className="e-template-modal-footer">
                <Typography className="e-template-selected-count">
                    {selected.length} task{selected.length !== 1 ? "s" : ""} selected
                </Typography>
                <Box className="e-template-modal-actions">
                    <Button onClick={onClose} className="e-template-cancel-btn">
                        {voiceiiTags("Cancel")}
                    </Button>
                    <Button
                        variant="contained"
                        disabled={selected.length === 0}
                        onClick={handleAdd}
                        className="e-template-add-btn"
                    >
                        {voiceiiTags("Add Selected Tasks")}
                    </Button>
                </Box>
            </Box>
        </Dialog>
    )
}

/* -------------------- Category Card -------------------- */

const CategoryCard = ({
    categorySchema,
    categoryData,
    taskSchema,
    onUpdateCategory,
    onAddTask,
    onAddTemplateTasks,
    onDeleteCategory,
    onUpdateTask,
    onDeleteTask,
}) => {
    const [openTemplate, setOpenTemplate] = useState(false)
    const { voiceiiTags } = useLocalization()

    const addCustomTask = () => {
        const durationField = taskSchema.fields?.find((f) => f.key === "duration_minutes")

        onAddTask(categoryData.id, {
            id: `task-${Date.now()}-${Math.random()}`,
            task_name: "",
            instructions: "",
            duration_minutes: durationField?.default || 15,
            status: "Pending",
        })
    }

    const addTemplateTasks = (tasks) => {
        const mapped = tasks.map((t) => ({
            id: `task-${Date.now()}-${Math.random()}`,
            ...t,
        }))
        onAddTemplateTasks(categoryData.id, mapped)
    }

    // Calculate total duration and task count
    const totalDuration = categoryData.tasks.reduce(
        (sum, task) => sum + (task.duration_minutes || 0),
        0,
    )
    const taskCount = categoryData.tasks.length

    return (
        <Box className="e-category-card">
            {/* Header */}
            <Box className="e-category-header">
                <Box className="e-category-header-left">
                    <TextField
                        className="e-category-name-input"
                        size="small"
                        placeholder={voiceiiTags("Category")}
                        value={categoryData.category_name}
                        onChange={(e) => onUpdateCategory(categoryData.id, e.target.value)}
                    />
                    {taskCount > 0 && (
                        <Typography className="e-category-summary">
                            {totalDuration} min • {taskCount} task{taskCount !== 1 ? "s" : ""}
                        </Typography>
                    )}
                </Box>

                <Box className="e-category-header-right">
                    {categorySchema?.template_tasks?.length > 0 && (
                        <Button
                            variant="contained"
                            startIcon={<AddIcon />}
                            onClick={() => setOpenTemplate(true)}
                            className="e-add-tasks-from-template"
                        >
                            <span className="e-button-text">
                                {voiceiiTags("Add Tasks from Template")}
                            </span>
                        </Button>
                    )}

                    <Button
                        startIcon={<AddIcon />}
                        onClick={addCustomTask}
                        className="e-add-custom-task-btn"
                    >
                        <span className="e-button-text">{voiceiiTags("Add Custom Task")}</span>
                    </Button>

                    <IconButton
                        onClick={() => onDeleteCategory(categoryData.id)}
                        className="e-category-delete-btn"
                    >
                        <DeleteIcon />
                    </IconButton>
                </Box>
            </Box>

            {/* Tasks */}
            {categoryData.tasks.length === 0 ? (
                <Typography className="e-category-empty-message">
                    {voiceiiTags("No tasks in this category")}
                </Typography>
            ) : (
                categoryData.tasks.map((task) => (
                    <TaskInlineForm
                        key={task.id}
                        task={task}
                        taskSchema={taskSchema}
                        onUpdate={(t) => onUpdateTask(categoryData.id, t)}
                        onDelete={() => onDeleteTask(categoryData.id, task.id)}
                    />
                ))
            )}

            {/* Template Modal */}
            {categorySchema?.template_tasks?.length > 0 && (
                <TemplateTaskModal
                    open={openTemplate}
                    onClose={() => setOpenTemplate(false)}
                    templateTasks={categorySchema.template_tasks}
                    onAdd={addTemplateTasks}
                />
            )}
        </Box>
    )
}

/* -------------------- MAIN COMPONENT -------------------- */

const MultiSelectFormField = ({ field, value, onChange, error }) => {
    const { voiceiiTags } = useLocalization()

    const schemaCategories = useMemo(() => {
        return field?.details?.validation?.possible_value || []
    }, [field])

    const taskSchema = field?.details?.task_schema || {
        fields: [
            { key: "task_name" },
            { key: "duration_minutes", default: 15, options: [5, 15, 30] },
            { key: "instructions" },
        ],
    }

    const [categories, setCategories] = useState(() => {
        // If value exists and is an array (even if empty), use it (preserves backend data structure)
        if (Array.isArray(value) && value.length > 0) {
            // Ensure all categories have proper structure
            return value.map((category) => ({
                ...category,
                id: category.id || `category-${category.category_id}`,
                tasks: Array.isArray(category.tasks) ? category.tasks : [],
            }))
        }

        // If value is null/undefined or empty array, initialize from schema structure
        // This ensures we always have the raw schema structure even when no tasks are selected
        if (Array.isArray(schemaCategories) && schemaCategories.length > 0) {
            return schemaCategories.map((c) => ({
                id: `category-${c.category_id}`,
                category_id: c.category_id,
                category_name: c.category_name,
                tasks: [],
            }))
        }

        // Fallback: return empty array if no schema
        return []
    })

    const isInitial = useRef(true)

    // Sync categories when value changes from parent (e.g. template injection or de-select restore)
    useEffect(() => {
        if (!Array.isArray(value)) return
        setCategories((prev) => {
            // Avoid overwriting if we already have equivalent data (prevents sync loops)
            if (JSON.stringify(prev) === JSON.stringify(value)) return prev

            // Handle empty array (e.g. restore on de-select when previous was empty)
            if (value.length === 0) {
                if (Array.isArray(schemaCategories) && schemaCategories.length > 0) {
                    return schemaCategories.map((c) => ({
                        id: `category-${c.category_id}`,
                        category_id: c.category_id,
                        category_name: c.category_name,
                        tasks: [],
                    }))
                }
                return []
            }

            // Ensure all categories have proper IDs if missing
            return value.map((category) => ({
                ...category,
                id: category.id || `category-${category.category_id}`,
                tasks: Array.isArray(category.tasks)
                    ? category.tasks.map((task) => ({
                          ...task,
                          id: task.id || `task-${Date.now()}-${Math.random()}`,
                          task_name: task.task_name || "",
                          instructions: task.instructions || "",
                          duration_minutes: task.duration_minutes || 15,
                          status: task.status || "Pending",
                      }))
                    : [],
            }))
        })
    }, [value, schemaCategories])

    useEffect(() => {
        if (isInitial.current) {
            isInitial.current = false
            // On initial mount, always send the categories structure (even if empty)
            // This ensures the raw schema structure is preserved
            onChange(categories)
            return
        }
        // Only call onChange when categories actually change to prevent unnecessary re-renders
        onChange(categories)
    }, [categories, onChange])

    const updateCategory = (id, name) => {
        setCategories((prev) => prev.map((c) => (c.id === id ? { ...c, category_name: name } : c)))
    }

    const addTask = (categoryId, task) => {
        setCategories((prev) =>
            prev.map((c) => (c.id === categoryId ? { ...c, tasks: [...c.tasks, task] } : c)),
        )
    }

    const addTemplateTasks = (categoryId, tasks) => {
        setCategories((prev) =>
            prev.map((c) => (c.id === categoryId ? { ...c, tasks: [...c.tasks, ...tasks] } : c)),
        )
    }

    const updateTask = (categoryId, task) => {
        setCategories((prev) =>
            prev.map((c) =>
                c.id === categoryId
                    ? {
                          ...c,
                          tasks: c.tasks.map((t) => (t.id === task.id ? task : t)),
                      }
                    : c,
            ),
        )
    }

    const deleteTask = (categoryId, taskId) => {
        setCategories((prev) =>
            prev.map((c) =>
                c.id === categoryId
                    ? {
                          ...c,
                          tasks: c.tasks.filter((t) => t.id !== taskId),
                      }
                    : c,
            ),
        )
    }

    const deleteCategory = (categoryId) => {
        setCategories((prev) => prev.filter((c) => c.id !== categoryId))
    }

    const addCategory = () => {
        const newCategory = {
            id: `category-${Date.now()}-${Math.random()}`,
            category_id: `custom-${Date.now()}`,
            category_name: "",
            tasks: [],
        }
        setCategories((prev) => [...prev, newCategory])
    }

    const getCategorySchema = (categoryData) =>
        schemaCategories.find((c) => c.category_id === categoryData.category_id)

    return (
        <Box>
            <Box className="e-category-header-section">
                {field?.name && (
                    <Typography className="e-asset-label">{voiceiiTags(field.name)}</Typography>
                )}
                <Button
                    startIcon={<AddIcon />}
                    onClick={addCategory}
                    className="e-add-category-btn"
                >
                    <span className="e-button-text">{voiceiiTags("Add Category")}</span>
                </Button>
            </Box>

            {categories.map((cat) => (
                <CategoryCard
                    key={cat.id}
                    categorySchema={getCategorySchema(cat)}
                    categoryData={cat}
                    taskSchema={taskSchema}
                    onUpdateCategory={updateCategory}
                    onAddTask={addTask}
                    onAddTemplateTasks={addTemplateTasks}
                    onDeleteCategory={deleteCategory}
                    onUpdateTask={updateTask}
                    onDeleteTask={deleteTask}
                />
            ))}

            {error && <Typography className="e-input-error">{error}</Typography>}
        </Box>
    )
}

MultiSelectFormField.displayName = "MultiSelectFormField"

export default MultiSelectFormField
